import type { Property } from "@/interfaces/propertyinterface";

const ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;

/** Pull the 11-character YouTube id out of watch, youtu.be, shorts or embed links. */
export function youtubeId(url: string): string | null {
  if (!url) return null;
  let parsed: URL;
  try {
    parsed = new URL(url.trim());
  } catch {
    return null;
  }
  const host = parsed.hostname.replace(/^www\.|^m\./, "");
  if (!["youtube.com", "youtube-nocookie.com", "youtu.be"].includes(host)) return null;

  const fromQuery = parsed.searchParams.get("v");
  if (fromQuery && ID_PATTERN.test(fromQuery)) return fromQuery;

  const segments = parsed.pathname.split("/").filter(Boolean);
  const candidate =
    host === "youtu.be" ? segments[0] : ["shorts", "embed", "v", "live"].includes(segments[0]) ? segments[1] : undefined;
  return candidate && ID_PATTERN.test(candidate) ? candidate : null;
}

export function youtubeThumb(id: string, size: "hq" | "mq" = "hq") {
  return `https://i.ytimg.com/vi/${id}/${size}default.jpg`;
}

export function youtubeEmbedUrl(id: string) {
  return `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
}

export function youtubeWatchUrl(id: string) {
  return `https://www.youtube.com/watch?v=${id}`;
}

export type ProjectVideo = { id: string; url: string };

/** Valid YouTube videos on a project, in the order the backend lists them. */
export function projectVideos(property: Pick<Property, "youtube_videos">): ProjectVideo[] {
  return (property.youtube_videos ?? []).flatMap((url) => {
    const id = youtubeId(url);
    return id ? [{ id, url }] : [];
  });
}
